import { useActiveCircleId } from '@/hooks/use-active-circle';
import { invalidateClinicalPacks } from '@/hooks/use-clinical-packs';
import { usePackContext } from '@/hooks/use-pack-context';
import { useTrackSessionEvent } from '@/hooks/use-send-amplitude-event';
import { useUpdateCircleRecommendationStatus } from '@/hooks/use-update-circle-recommendation-status';
import { getGuideId } from '@/utils/media';
import { MediaGuideMetadata } from '@/utils/media/types';
import { routes } from '@/utils/routes';
import {
    EnumProductAnalyticsEvent,
    EventProperties,
} from '@calm/evexia-analytics';
import { RbcStatus } from '@calm/evexia-web-sdk';
import { PlayCircleOutline } from '@mui/icons-material';
import { useFavoriteGuide } from '@rhg/hooks/use-favorite-guide';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import useIsContentLocked from '@rhg/hooks/use-is-content-locked';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUnsponsoredDialog } from '@rhg/hooks/use-unsponsored-dialog';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { logger } from '@rhg/libs/logger';
import {
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
} from '@rhg/libs/material';
import { InfoOutline } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { getProgramRoute } from '@rhg/utils/programs';
import { PropsWithChildren, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FavoriteMenuItem as SharedFavoriteMenuItem } from '../favorite-button';
import { LearnWhyButton } from '../learn-why-button';
import {
    MoreMenu,
    MoreMenuButtonVariant,
    MoreMenuProps,
} from '../ui/more-menu';
import { FeedbackMenu } from './feedback';

export const enum CardMoreMenuType {
    Program = 'program',
    Pack = 'pack',
    Guide = 'guide',
}

export const CardMoreMenuItem = {
    Favorite: 'favorite',
    Feedback: 'feedback',
    LearnWhy: 'learnWhy',
    Play: 'play',
    ViewProgram: 'viewProgram',
    Children: 'children',
} as const;
export type CardMoreMenuItem =
    (typeof CardMoreMenuItem)[keyof typeof CardMoreMenuItem];

export const defaultCardMoreMenuItems = [
    CardMoreMenuItem.ViewProgram,
    CardMoreMenuItem.Play,
    CardMoreMenuItem.Favorite,
    CardMoreMenuItem.LearnWhy,
    CardMoreMenuItem.Children,
    CardMoreMenuItem.Feedback,
];

const styleMoreButton = {
    p: 0,
};

interface ViewProgramMenuItemProps {
    type: CardMoreMenuType;
    detailsLabel?: string;
    detailsRoute?: string;
    sendClickAnalytics?: () => void;
}

function ViewProgramMenuItem({
    type,
    detailsLabel,
    detailsRoute,
    sendClickAnalytics,
}: ViewProgramMenuItemProps) {
    const { formatMessage } = useIntl();
    const { program, packClass, recommendation } =
        useProvidedProgramContextWithGuidesAndWorkflow();

    const { updateStatusAsync } = useUpdateCircleRecommendationStatus(
        recommendation?.recDefnId
    );

    const route = useMemo(() => {
        // Callers can override the destination (e.g. the Mindshift banner
        // points its "All Mindshifts" item at the curated Series page).
        if (detailsRoute) {
            return detailsRoute;
        }
        if (type === CardMoreMenuType.Pack) {
            return routes.discoverPack(packClass);
        }
        if (type === CardMoreMenuType.Program) {
            return getProgramRoute(program, packClass);
        }
        // do not show view details button for guides
        return undefined;
    }, [detailsRoute, packClass, program, type]);

    if (!route) {
        return null;
    }

    // TODO(EVE-10506): failure logs to Datadog only; needs user-facing error UX
    const onViewProgramClicked = () => {
        // Fire-and-forget — the MenuItem renders as an anchor, so the browser
        // navigates as soon as this handler returns. Awaiting the status update
        // would let navigation outrun it; the .catch preserves error logging.
        updateStatusAsync({ status: RbcStatus.Viewed }).catch((err) =>
            logger.error(
                'Failed to mark recommendation as viewed',
                {},
                err as Error
            )
        );
        sendClickAnalytics?.();
    };

    return (
        <MenuItem
            href={route}
            onClick={onViewProgramClicked}
            trackButton={
                type === CardMoreMenuType.Program
                    ? 'ProgramCardViewProgram'
                    : 'NotMapped'
            }
        >
            <ListItemIcon>
                <InfoOutline />
            </ListItemIcon>
            <ListItemText>
                <Typography variant="body1" color="text.body">
                    {detailsLabel ??
                        formatMessage(commonMessages['common.view_program'])}
                </Typography>
            </ListItemText>
        </MenuItem>
    );
}

interface PlayMenuItemProps {
    type: CardMoreMenuType;
    playLabel?: string;
    sessionOverride?: MediaGuideMetadata;
    sendClickAnalytics?: () => void;
}

function PlayMenuItem({
    type,
    playLabel,
    sessionOverride,
    sendClickAnalytics,
}: PlayMenuItemProps) {
    const { formatMessage } = useIntl();
    const { openGuidePlayer } = useGuidePlayer();
    const { getIsContentLocked } = useIsContentLocked();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { showRegistrationDialog } = useRegistrationDialog();
    const {
        programState,
        programType,
        currentSession,
        packClass,
        workflow,
        toolsByGuide,
        recommendation,
    } = useProvidedProgramContextWithGuidesAndWorkflow();

    const { updateStatusAsync } = useUpdateCircleRecommendationStatus(
        recommendation?.recDefnId
    );

    const guide = sessionOverride ?? currentSession;

    const onPlayNextClicked = async () => {
        if (!guide) {
            return;
        }
        // For guides, packClass is optional (guides may not have one)
        // For programs and packs, packClass is required
        if (type !== CardMoreMenuType.Guide && !packClass) {
            return;
        }

        const isLocked = getIsContentLocked({ programState, programType });
        if (isLocked) {
            if (await showUnsponsoredDialog()) {
                showRegistrationDialog({
                    currentStep: RegistrationFlowStep.AccessCode,
                    registrationFlow: RegistrationFlow.ExistingRegistration,
                }).catch((err) =>
                    logger.error(
                        'Failed to show registration dialog',
                        {},
                        err as Error
                    )
                );
            }
            return;
        }

        sendClickAnalytics?.();

        // TODO(EVE-10506): failure logs to Datadog only; needs user-facing error UX
        updateStatusAsync({ status: RbcStatus.Viewed }).catch((err) =>
            logger.error(
                'Failed to mark recommendation as viewed',
                {},
                err as Error
            )
        );

        void openGuidePlayer({
            guide,
            packClass,
            workflow,
            tools: toolsByGuide?.[getGuideId(guide)],
            willSkipToPreviousProgress: true,
        });
    };

    return (
        <MenuItem
            onClick={() => {
                onPlayNextClicked().catch((err) =>
                    logger.error('Failed to play session', {}, err as Error)
                );
            }}
            trackButton={
                type === CardMoreMenuType.Program ? 'ProgramPlay' : 'NotMapped'
            }
        >
            <ListItemIcon>
                <PlayCircleOutline />
            </ListItemIcon>
            <ListItemText>
                <Typography variant="body1" color="text.body">
                    {playLabel ??
                        formatMessage(commonMessages['common.play_session'])}
                </Typography>
            </ListItemText>
        </MenuItem>
    );
}

interface FavoriteMenuItemProps {
    sessionOverride?: MediaGuideMetadata;
}

function FavoriteMenuItem({ sessionOverride }: FavoriteMenuItemProps) {
    const { activeCircleId } = useActiveCircleId();
    const { programState, currentSession, program, packClass } =
        useProvidedProgramContextWithGuidesAndWorkflow();

    const guide = sessionOverride ?? currentSession;

    const onFavoriteSuccess = () => {
        if (program?.isClinical && packClass) {
            invalidateClinicalPacks({
                packClasses: [packClass],
                circleId: activeCircleId,
            }).catch((err) =>
                logger.error(
                    'Failed to invalidate clinical packs',
                    {},
                    err as Error
                )
            );
        }
    };

    const { favoriteGuide, canFavorite } = useFavoriteGuide({
        programType: program?.isClinical ? 'clinical_program' : undefined,
        programState,
        onSuccess: onFavoriteSuccess,
    });

    return (
        <SharedFavoriteMenuItem
            guide={guide}
            favoriteGuide={favoriteGuide}
            canFavorite={canFavorite}
            trackButtonFavorite="GuideItemFavorite"
            trackButtonUnfavorite="GuideItemUnfavorite"
        />
    );
}

interface FeedbackMenuItemProps {
    type: CardMoreMenuType;
    sessionOverride?: MediaGuideMetadata;
}

function FeedbackMenuItem({ type, sessionOverride }: FeedbackMenuItemProps) {
    const { currentSession, program } =
        useProvidedProgramContextWithGuidesAndWorkflow();

    const guide = sessionOverride ?? currentSession;

    if (!guide) {
        return null;
    }

    return (
        <FeedbackMenu
            contentId={
                type === CardMoreMenuType.Program
                    ? program?.programId ?? ''
                    : getGuideId(guide)
            }
        />
    );
}

interface LearnWhyMenuItemProps {
    isTopRecommendation?: boolean;
}

function LearnWhyMenuItem({ isTopRecommendation }: LearnWhyMenuItemProps) {
    const { recommendation, program, packProgram } =
        useProvidedProgramContextWithGuidesAndWorkflow();

    return (
        <LearnWhyButton
            isTopRecommendation={isTopRecommendation}
            recommendation={recommendation}
            program={program}
            packProgram={packProgram}
            variant="menuItem"
        />
    );
}

export type CardMoreMenuProps = PropsWithChildren<{
    buttonColor?: MoreMenuProps['buttonColor'];
    buttonVariant?: MoreMenuProps['buttonVariant'];
    buttonSize?: MoreMenuProps['buttonSize'];
    buttonSx?: MoreMenuProps['buttonSx'];
    /** See `MoreMenuProps['rootSx']` - the stacking-order escape hatch. */
    rootSx?: MoreMenuProps['rootSx'];
    actionsLabel?: string;
    detailsLabel?: string;
    detailsRoute?: string;
    playLabel?: string;
    sessionOverride?: MediaGuideMetadata;
    isTopRecommendation?: boolean;
    type: CardMoreMenuType;
    items?: CardMoreMenuItem[];
    trackProps?: EventProperties;
}>;

export function CardMoreMenu({
    buttonColor = 'default',
    buttonVariant = MoreMenuButtonVariant.Flat,
    buttonSize = 'small',
    buttonSx = styleMoreButton,
    rootSx,
    type,
    items = defaultCardMoreMenuItems,
    actionsLabel,
    detailsLabel,
    detailsRoute,
    playLabel,
    sessionOverride,
    isTopRecommendation,
    children,
    trackProps,
}: CardMoreMenuProps) {
    const { formatMessage } = useIntl();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const { program, currentSession } =
        useProvidedProgramContextWithGuidesAndWorkflow();
    const packTitle = usePackContext();

    const sendClickAnalytics = useCallback(() => {
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemClicked,
            guide: currentSession,
            program,
            packTitle,
            trackProps,
        });
    }, [trackPackItemEvent, program, packTitle, trackProps, currentSession]);

    const renderMenuItem = (item: CardMoreMenuItem) => {
        switch (item) {
            case CardMoreMenuItem.ViewProgram:
                return (
                    <ViewProgramMenuItem
                        key={item}
                        type={type}
                        detailsLabel={detailsLabel}
                        detailsRoute={detailsRoute}
                        sendClickAnalytics={sendClickAnalytics}
                    />
                );
            case CardMoreMenuItem.Play:
                return (
                    <PlayMenuItem
                        key={item}
                        type={type}
                        playLabel={playLabel}
                        sessionOverride={sessionOverride}
                        sendClickAnalytics={sendClickAnalytics}
                    />
                );
            case CardMoreMenuItem.Favorite:
                return (
                    <FavoriteMenuItem
                        key={item}
                        sessionOverride={sessionOverride}
                    />
                );
            case CardMoreMenuItem.Feedback:
                return (
                    <FeedbackMenuItem
                        key={item}
                        type={type}
                        sessionOverride={sessionOverride}
                    />
                );
            case CardMoreMenuItem.LearnWhy:
                return (
                    <LearnWhyMenuItem
                        key={item}
                        isTopRecommendation={isTopRecommendation}
                    />
                );
            case CardMoreMenuItem.Children:
                return children;
            default:
                return null;
        }
    };

    return (
        <MoreMenu
            buttonSize={buttonSize}
            buttonVariant={buttonVariant}
            buttonSx={sxAppend(styleMoreButton, buttonSx)}
            buttonColor={buttonColor}
            rootSx={rootSx}
            buttonLabel={
                actionsLabel ??
                formatMessage(programMessages['program.program_actions'])
            }
            trackButton={
                type === CardMoreMenuType.Program ? 'ProgramMore' : undefined
            }
        >
            {items.map(renderMenuItem)}
        </MoreMenu>
    );
}
